import React from 'react';
import { CandidatesState, MatchScore } from './types';
import { TCandidate } from './types';
import { Box, CircularProgress, IconButton, TableCell } from '@mui/material';
import { classes } from '../Components/GenericTable/styles';
import { AccessTimeFilled, Close, Check, DoneAll, LinkedIn, Visibility } from '@mui/icons-material';
import { theme } from '../../ThemeContext/ThemeObject';
import recruitLogo from '../AgenticMatching/recruit-logo.png';

interface CandidatesTableRowsProps {
  column: CandidatesState['candidatesTableState']['columns'][number];
  item: TCandidate;
  tableHovered: boolean;
  onShowDetails?: (candidate: TCandidate) => void;
  onExpand?: () => void;
  onRevealContacts?: (candidate: TCandidate) => void;
}

const CandidatesTableRows = ({
  column,
  item,
  tableHovered,
  onShowDetails,
  onExpand,
  onRevealContacts
}: CandidatesTableRowsProps) => {
  if (!column.enabled) return null;
  const contactsRevealed = item.candidate_group.contacts_revealed;
  const showEmails = contactsRevealed || item.candidate_group.emails.length > 0;
  const showPhones = contactsRevealed || item.candidate_group.phones.length > 0;

  const sourceIcon = (source: string) => {
    switch (source) {
      case 'linkedin':
        return <LinkedIn color="primary" />;
      default:
        return <img src={recruitLogo} alt="Recruit" style={{ width: '25px', height: '25px' }} />;
    }
  };

  const sourceDisplay = (candidate: { source: string, url: string }) => {
    return (
      <Box>
        <a href={candidate.url} target="_blank" rel="noopener noreferrer">
          {sourceIcon(candidate.source)}
        </a>
      </Box>
    );
  };

  const matchingConditionIndicator = (conditionId: number) => {
    const matchingCondition = item.matching_conditions.find((cond) => cond.id === conditionId);
    if (
      !matchingCondition ||
      matchingCondition.matched_score === null ||
      matchingCondition.matched_score === undefined
    ) {
      return <AccessTimeFilled color="disabled" />;
    }

    switch (matchingCondition.matched_score) {
      case MatchScore.EXCELLENT:
        return <DoneAll sx={{ color: theme.palette.success.main }} />;
      case MatchScore.GOOD:
        return <Check sx={{ color: theme.palette.secondary.yellow }} />;
      default:
        return <Close sx={{ color: theme.palette.error.main }} />;
    }
  };

  if (column.id.startsWith('condition_')) {
    const conditionId = parseInt(column.id.split('_')[1]);
    return (
      <TableCell
        key={item.id + column.id}
        onClick={(e) => {
          e.stopPropagation();
          if (onExpand) {
            onExpand();
          }
        }}
        sx={{
          cursor: onShowDetails || onExpand ? 'pointer' : 'default'
        }}
      >
        {matchingConditionIndicator(conditionId)}
      </TableCell>
    );
  }

  switch (column.id) {
    case 'id':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            ...classes.sticky,
            ...classes.itemTitleTableCell,
            left: '58px',
            ...(tableHovered && classes.scrollShadowVisible),
            width: '100px',
            minWidth: '120px'
          }}
        >
          {item.id}
        </TableCell>
      );
    case 'name':
      return <TableCell key={item.id + column.id}>{item.candidate_group.name}</TableCell>;
    case 'sources':
      return (
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            {item.candidate_group.candidates.map((candidate) => sourceDisplay(candidate))}
          </Box>
        </TableCell>
      );
    case 'matching_percent':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            cursor: onShowDetails ? 'pointer' : 'default'
          }}
          onClick={() => onShowDetails && onShowDetails(item)}
        >
          <span style={{ color: item.matching_percent_color }}>
            {item.matching_percent !== null && item.matching_percent !== undefined
              ? `${item.matching_percent}%`
              : 'Calculating...'}
          </span>
        </TableCell>
      );
    case 'emails':
      return (
        <TableCell>
          <TableCell>
            {!showEmails && !contactsRevealed && !item.revealing_contacts && (
              <IconButton onClick={() => onRevealContacts && onRevealContacts(item)}>
                <Visibility />
              </IconButton>
            )}
            {showEmails && <span>{item.candidate_group.emails.join(', ')}</span>}
            {item.revealing_contacts && <CircularProgress size={20} />}
          </TableCell>
        </TableCell>
      );
    case 'phones':
      return (
        <TableCell>
          <TableCell>
            {!showPhones && !contactsRevealed && !item.revealing_contacts && (
              <IconButton onClick={() => onRevealContacts && onRevealContacts(item)}>
                <Visibility />
              </IconButton>
            )}
            {showPhones && <span>{item.candidate_group.phones.join(', ')}</span>}
            {item.revealing_contacts && <CircularProgress size={20} />}
          </TableCell>
        </TableCell>
      );
    default:
      return <TableCell></TableCell>;
  }
};

export default CandidatesTableRows;
